'use client'

import type { ReactNode } from 'react'
import type { Address } from 'viem'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Text
} from '@repo/ui'
import { ArrowDiagonalUp, Copy, Info } from '@repo/ui/icons'
import { toast } from 'sonner'

import type { ChainId, Token, WithChainId } from '../../web3.types'

import { getBlockExplorerAddressUrl } from '../../utils/block-explorer.utils'
import { getChainShortName } from '../../utils/chain.utils'
import { TokenIcon } from '../TokenIcon'
import { useTokenSelectContext } from './TokenSelectProvider'

function compactAddress(address: Address) {
  return `${address.slice(0, 4)}…${address.slice(-4)}`
}

interface TokenSelectListProps {
  testIdPrefix?: string
  alwaysShowChainBadge?: boolean
  /** Right-aligned column per row; callers render a 15px/22px first line and a 13px second line. */
  renderTrailing?: (token: WithChainId<Token>) => ReactNode
  /** Renders the chain's short name on the row's second line. */
  showChainName?: boolean
  /** Replaces the default "No tokens found" state when no row matches the search. */
  emptyState?: ReactNode
}

export function TokenSelectList({
  testIdPrefix,
  alwaysShowChainBadge = false,
  showChainName = false,
  renderTrailing,
  emptyState
}: TokenSelectListProps) {
  const { filteredTokens, multiChain, onTokenSelect } = useTokenSelectContext()
  const showChainBadge = multiChain || alwaysShowChainBadge

  if (filteredTokens.length === 0) {
    if (emptyState !== undefined) return <>{emptyState}</>
    return (
      <div className="px-sm py-xs gap-2xs flex flex-col items-center text-center">
        <Text className="text-foreground" size="2xs">
          No tokens found
        </Text>
        <Text className="text-secondary-foreground" size="4xs">
          Try a different search
        </Text>
      </div>
    )
  }

  return (
    <div>
      {filteredTokens.map(token => (
        <TokenSelectItem
          key={`${token.address}-${token.chainId}`}
          token={token}
          onSelect={onTokenSelect}
          showChainBadge={showChainBadge}
          showChainName={showChainName}
          testId={testIdPrefix ? `${testIdPrefix}-${token.symbol}` : undefined}
          trailing={renderTrailing?.(token)}
        />
      ))}
    </div>
  )
}

type TokenSelectItemProps = {
  token: WithChainId<Token>
  onSelect: (token: WithChainId<Token>) => void
  showChainBadge: boolean
  showChainName?: boolean
  testId?: string
  trailing?: ReactNode
}

function TokenSelectItem({
  token,
  onSelect,
  showChainBadge,
  showChainName = false,
  testId,
  trailing
}: TokenSelectItemProps) {
  return (
    <Button
      type="button"
      onClick={() => onSelect(token)}
      variant="ghost"
      className="flex h-fit w-full gap-3 rounded-sm px-3 py-2 text-left"
      data-testid={testId}
      data-chain-id={token.chainId}
    >
      <TokenIcon
        address={token.address}
        symbol={token.symbol}
        logoUrl={token.logoUrl}
        className="size-10"
        chainId={showChainBadge ? token.chainId : undefined}
      />
      <div className="flex w-0 grow justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <Text size="sm" className="text-foreground truncate leading-5.5">
            {token.symbol}
          </Text>
          <div className="flex min-w-0 items-center gap-1">
            <Text
              size="xs"
              className="text-secondary-foreground min-w-0 truncate"
              title={token.name}
            >
              {token.name}
            </Text>
            <Text
              size="xs"
              className={
                showChainName
                  ? 'text-muted-foreground hidden min-w-0 shrink-[999] truncate sm:block'
                  : 'text-muted-foreground min-w-0 shrink-[999] truncate'
              }
              tag="span"
              title={token.address}
            >
              {compactAddress(token.address)}
            </Text>
            {showChainName && (
              <Text size="xs" className="text-muted-foreground shrink-0" tag="span">
                · {getChainShortName(token.chainId)}
              </Text>
            )}
            <TokenAddressActions address={token.address} chainId={token.chainId} />
          </div>
        </div>
        {trailing != null && (
          <div
            className="flex shrink-0 flex-col items-end text-right"
            data-testid={testId ? `${testId}-balance` : undefined}
          >
            {trailing}
          </div>
        )}
      </div>
    </Button>
  )
}

type TokenAddressActionsProps = {
  address: Address
  chainId: ChainId
}

function TokenAddressActions({ address, chainId }: TokenAddressActionsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
        <div
          role="button"
          className="text-muted-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground -my-0.5 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full p-0.5 transition-colors"
        >
          <Info className="size-5" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]">
        <DropdownMenuItem
          onClick={async e => {
            e.stopPropagation()
            try {
              await navigator.clipboard.writeText(address)
              toast.success('Address copied to clipboard')
            } catch {
              toast.error('Failed to copy address')
            }
          }}
        >
          <Copy className="size-5" />
          Copy address
        </DropdownMenuItem>
        <DropdownMenuItem asChild onClick={e => e.stopPropagation()}>
          <a
            href={getBlockExplorerAddressUrl(address, chainId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ArrowDiagonalUp className="size-5" />
            View Details
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
